// @ts-nocheck
// app/articles/index.tsx — Editorial articles index

import { useEffect, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import { Link, Stack, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { ArrowLeft, Search } from 'lucide-react-native';

import { theme, ACCENT, INK, INK_FAINT, INK_MUTED, INK_SOFT, PAPER, PAPER_SOFT } from '@/constants/theme';
import { EditorialButton, EditorialFooter, Eyebrow, Headline, Numeral, Paper, PageHero, Section, useEditorialBreakpoint } from '@/components/editorial';
import { EditorialHeader } from '@/components/editorial/EditorialHeader';
import { AccessibilityButton } from '@/components/AccessibilityButton';
import { supabase } from '@/utils/supabase';

const MONO = theme.fonts.mono;
const SERIF = theme.fonts.serif;
const SERIF_BLACK = theme.fonts.serifBlack;
const SERIF_BOLD = theme.fonts.serifBold;
const SANS = theme.fonts.regular;
const SANS_MED = theme.fonts.medium;
const SANS_BOLD = theme.fonts.bold;
const IS_WEB = Platform.OS === 'web';

interface ArticlePreview {
  id: string; slug: string; title: string;
  hero_intro: string; category: string;
  published_at: string; view_count: number;
}

const CATEGORIES = [
  { id: 'all', name: 'הכל' }, { id: 'general', name: 'כללי' }, { id: 'tech', name: 'הייטק' },
  { id: 'qa', name: 'QA' }, { id: 'digital', name: 'דיגיטל' }, { id: 'stocks', name: 'שוק ההון' },
  { id: 'ai', name: 'AI' }, { id: 'data', name: 'דאטה' }, { id: 'comparison', name: 'השוואה' },
  { id: 'audience', name: 'קהל יעד' }, { id: 'career', name: 'קריירה' },
];

export default function ArticlesListPage() {
  const router = useRouter();
  const { isDesktop } = useEditorialBreakpoint();
  const [articles, setArticles] = useState<ArticlePreview[]>([]);
  const [filteredArticles, setFilteredArticles] = useState<ArticlePreview[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('articles')
          .select('id, slug, title, hero_intro, category, published_at, view_count')
          .eq('published', true)
          .order('published_at', { ascending: false });
        if (error) throw error;
        setArticles(data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    let filtered = articles;
    if (selectedCategory !== 'all') filtered = filtered.filter(a => a.category === selectedCategory);
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      filtered = filtered.filter(a => a.title.toLowerCase().includes(q) || a.hero_intro.toLowerCase().includes(q));
    }
    setFilteredArticles(filtered);
  }, [selectedCategory, searchQuery, articles]);

  const itemListSchema = {
    '@context': 'https://schema.org', '@type': 'CollectionPage',
    name: 'מאמרים על קורסים',
    description: 'מרכז המאמרים של Kursi לבחירת קורסים, הסבות מקצועיות והשוואות לימודים.',
    url: 'https://kursiaii.com/articles', inLanguage: 'he',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredArticles.slice(0, 12).map((a, i) => ({
        '@type': 'ListItem', position: i + 1,
        url: `https://kursiaii.com/articles/${a.slug}`, name: a.title,
      })),
    },
  };

  return (
    <Paper>
      <AccessibilityButton />
      <Stack.Screen options={{ title: 'מאמרים — Kursi', headerShown: false }} />
      {IS_WEB && (
        <Head>
          <title>מרכז המאמרים · Kursi</title>
          <meta name="description" content="מדריכים ומאמרים לבחירת קורסים — תכנות, AI, דיגיטל מרקטינג, פסיכומטרי ועוד." />
          <link rel="canonical" href="https://kursiaii.com/articles" />
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }} />
        </Head>
      )}

      <ScrollView style={{ flex: 1, backgroundColor: PAPER }} contentContainerStyle={{ backgroundColor: PAPER }}>
        <EditorialHeader />

        <Section py="xl" px="md" max={1240}>
          <PageHero
            kicker="גיליון המאמרים · עיתון Kursi"
            title={<>מרכז הידע{'\n'}<Text style={{ color: ACCENT, fontStyle: 'italic' as any }}>של Kursi.</Text></>}
            lede="מאמרים מקצועיים, מדריכים מפורטים והשוואות שיעזרו לכם לבחור את הקורס המושלם. מתעדכן שבועית."
            meta={`${articles.length} מאמרים · ${CATEGORIES.length - 1} קטגוריות`}
          />

          <View style={s.searchBlock}>
            <Search color={INK} size={18} />
            <TextInput
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholder="חיפוש במאמרים…"
              placeholderTextColor={INK_MUTED}
              style={s.searchInput}
              accessibilityLabel="חיפוש מאמרים"
            />
          </View>

          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 18 }} contentContainerStyle={{ gap: 8 } as any}>
            {CATEGORIES.map((cat) => {
              const active = selectedCategory === cat.id;
              return (
                <Pressable
                  key={cat.id}
                  onPress={() => setSelectedCategory(cat.id)}
                  style={[s.chip, active && s.chipActive]}
                  accessibilityRole="button"
                >
                  <Text style={[s.chipText, active && { color: PAPER }]}>{cat.name}</Text>
                </Pressable>
              );
            })}
          </ScrollView>
        </Section>

        <Section py="md" px="md" max={1240}>
          {loading ? (
            <View style={{ paddingVertical: 60, alignItems: 'center' }}>
              <ActivityIndicator size="large" color={INK} />
            </View>
          ) : filteredArticles.length === 0 ? (
            <View style={{ paddingVertical: 60, alignItems: 'center' }}>
              <Eyebrow color={ACCENT}>אין מאמרים בקטגוריה הזאת</Eyebrow>
              <View style={{ height: 14 }} />
              <Headline level={2} align="center">תנסו קטגוריה אחרת.</Headline>
            </View>
          ) : (
            <View style={s.grid}>
              {filteredArticles.map((article, i) => (
                <Link
                  key={article.id}
                  href={`/articles/${article.slug}`}
                  style={[s.cardLink, { width: isDesktop ? '33.333%' : '100%' }] as any}
                  accessibilityRole="link"
                >
                  <View style={s.cardInner}>
                    <View style={{ flexDirection: 'row-reverse', alignItems: 'baseline', justifyContent: 'space-between' } as any}>
                      <Numeral size={36}>{String(i + 1).padStart(2, '0')}</Numeral>
                      <Text style={s.cardCat}>
                        {CATEGORIES.find(c => c.id === article.category)?.name || article.category}
                      </Text>
                    </View>
                    <View style={{ height: 14 }} />
                    <Text style={s.cardTitle} numberOfLines={3}>{article.title}</Text>
                    <View style={{ height: 8 }} />
                    <Text style={s.cardIntro} numberOfLines={3}>{article.hero_intro}</Text>
                    <View style={{ height: 16 }} />
                    <View style={s.cardFooter}>
                      {article.view_count > 0 && (
                        <Text style={s.cardMeta}>{article.view_count} צפיות</Text>
                      )}
                      <Text style={s.readMore}>קרא →</Text>
                    </View>
                  </View>
                </Link>
              ))}
            </View>
          )}
        </Section>

        <EditorialFooter onNavigate={(href) => router.push(href as any)} />
      </ScrollView>
    </Paper>
  );
}

const s = StyleSheet.create({
  searchBlock: {
    flexDirection: 'row-reverse', alignItems: 'center', gap: 12,
    borderWidth: 1, borderColor: INK, paddingHorizontal: 18, paddingVertical: 14,
    backgroundColor: PAPER_SOFT, marginTop: 16,
  } as any,
  searchInput: {
    flex: 1, fontFamily: SANS_MED, fontSize: 16, color: INK,
    textAlign: 'right', backgroundColor: 'transparent',
    ...(IS_WEB ? { outlineStyle: 'none' } as any : {}),
  },

  chip: {
    paddingVertical: 7, paddingHorizontal: 14,
    borderWidth: 1, borderColor: INK, backgroundColor: 'transparent',
  },
  chipActive: { backgroundColor: INK },
  chipText: { fontFamily: MONO, fontSize: 11, color: INK, letterSpacing: 1.4, textTransform: 'uppercase' as any },

  grid: { flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 0 } as any,
  cardLink: { padding: 0, ...(IS_WEB ? { textDecorationLine: 'none' } as any : {}) } as any,
  cardInner: {
    padding: 28, borderWidth: 1, borderColor: INK, backgroundColor: PAPER_SOFT,
    marginLeft: -1, marginTop: -1, minHeight: 280,
    flexGrow: 1,
  },
  cardCat: { fontFamily: MONO, fontSize: 10, color: ACCENT, letterSpacing: 1.6, textTransform: 'uppercase' as any },
  cardTitle: { fontFamily: SERIF_BOLD, fontSize: 22, color: INK, letterSpacing: -0.3, textAlign: 'right', lineHeight: 28 },
  cardIntro: { fontFamily: SANS, fontSize: 14, color: INK_SOFT, lineHeight: 22, textAlign: 'right' },
  cardFooter: {
    flexDirection: 'row-reverse', justifyContent: 'space-between', alignItems: 'center',
    borderTopWidth: 1, borderTopColor: INK, paddingTop: 12, marginTop: 'auto' as any,
  } as any,
  cardMeta: { fontFamily: MONO, fontSize: 10, color: INK_MUTED, letterSpacing: 1.2 },
  readMore: { fontFamily: MONO, fontSize: 11, color: ACCENT, letterSpacing: 1.4 },
});
